import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSetRecoilState } from 'recoil';

import {
  confirmCoupon,
  getGiftCouponById,
  shareCoupon,
  unshareCoupon,
  updateCouponRestrictedPhone,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import {
  GiftCouponForStore,
  GiftCouponRedeemStatus,
  GiftCouponShareStatus,
  ShareChannel,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import {
  confirmDelivery,
  getGiftDeliveryById,
  shareGiftDelivery,
  unshareGiftDelivery,
  updateDeliveryRestrictedPhone,
} from '@shared/api/giftDelivery/giftDelivery.controller';
import {
  GiftDeliveryForStore,
  GiftDeliveryShareChannel,
  GiftDeliveryShareStatus,
  GiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import { GiftItemForStore } from '@shared/api/giftItem/giftItem.interface';
import { giftOrderForStoreDataAtomFamily } from '@shared/api/giftOrder/giftOrder.atom';
import { updateGiftOrderCourtesy } from '@shared/api/giftOrder/giftOrder.controller';
import {
  GiftOrderDeliveryMode,
  GiftOrderForStore,
  GiftOrderItem,
  GiftOrderItemType,
  GiftStoreRole,
} from '@shared/api/giftOrder/giftOrder.interface';
import { Image } from '@shared/api/media/media.interface';
import { toastError } from '@shared/plugIn/toast';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { useQueryClient } from 'react-query';

import {
  loadPendingConfirmForOrder,
  removePendingConfirm,
  savePendingConfirm,
  SHARE_GUIDE_DISMISSED_KEY,
} from '../utils/giftOrderCoupon.utils';
import { useKakaoShareWebhookCheck } from './useKakaoShareWebhookCheck';

type PendingConfirmItem =
  | { type: 'coupon'; item: GiftCouponForStore }
  | { type: 'delivery'; item: GiftDeliveryForStore };

// 쿠폰의 ShareChannel 과 배송의 GiftDeliveryShareChannel 은 enum 정의는 분리되어 있지만
// 실제 string 값은 1:1 동일 ('KakaoTalk' | 'SMS' | 'Link' | 'Other') 하므로 단순 캐스팅.
const toDeliveryShareChannel = (channel: ShareChannel): GiftDeliveryShareChannel =>
  channel as unknown as GiftDeliveryShareChannel;

interface UseGiftOrderShareParams {
  orderId: string;
  order: GiftOrderForStore | null;
  role: GiftStoreRole;
  courtesyContent: string;
}

export function useGiftOrderShare({
  orderId,
  order,
  role,
  courtesyContent,
}: UseGiftOrderShareParams) {
  const setOrderData = useSetRecoilState(giftOrderForStoreDataAtomFamily(orderId));
  const queryClient = useQueryClient();

  const syncCourtesyIfNeeded = useCallback(async () => {
    if (!orderId || !courtesyContent || order?.courtesyContent === courtesyContent)
      return;
    try {
      const updated = await updateGiftOrderCourtesy(orderId, { courtesyContent }, role);
      setOrderData((prev) =>
        prev ? { ...prev, courtesyContent: updated.courtesyContent } : prev,
      );
    } catch (error) {
      console.error('답례글 자동 저장 실패:', error);
    }
  }, [orderId, courtesyContent, order?.courtesyContent, role, setOrderData]);

  // === 쿠폰 목록 (취소/폐기된 쿠폰 제외) ===
  const couponList = useMemo(() => {
    return ((order?.coupons ?? []) as GiftCouponForStore[]).filter(
      (c) =>
        c.redeemStatus !== GiftCouponRedeemStatus.Canceled &&
        c.redeemStatus !== GiftCouponRedeemStatus.Disposed,
    );
  }, [order]);

  // === 배송형 상품 ===
  const deliveryItems = useMemo(() => {
    if (!order?.orderDetails) return [];
    return order.orderDetails.filter(
      (item) => item.type === GiftOrderItemType.Delivery,
    ) as GiftOrderItem<GiftItemForStore, Image>[];
  }, [order]);

  const deliveryMode = order?.deliveryMode;
  const isIndividualDelivery = deliveryMode === GiftOrderDeliveryMode.Individual;
  const isBulkDelivery = deliveryMode === GiftOrderDeliveryMode.Bulk;

  const deliveryList = useMemo(() => {
    return ((order?.deliveries ?? []) as GiftDeliveryForStore[]).filter(
      (d) => d.deliveryStatus !== GiftDeliveryStatus.Canceled,
    );
  }, [order]);

  // === 공유 가이드 모달 ===
  const [showShareGuide, setShowShareGuide] = useState(false);
  const pendingShareRef = useRef<{
    type: 'coupon' | 'delivery';
    item: GiftCouponForStore | GiftDeliveryForStore;
  } | null>(null);

  // === 쿠폰 / 배송 공유 모달 (하단 시트) ===
  // 두 모달의 open 상태 setter 는 카카오 웹훅 onSuccess 안에서도 참조하므로
  // (= 카톡 다녀와서 ShareConfirmModal 띄울 때 하단 시트를 닫기 위함)
  // 훅 호출을 kakaoShareCheck 보다 위로 끌어올려 둔다.
  const couponShareModalOpenId = `gift-coupon-share-${orderId}`;
  const setCouponShareModalOpen = useSetRecoilState(
    simpleOpenAtomFamily(couponShareModalOpenId),
  );
  const deliveryShareModalOpenId = `gift-delivery-share-${orderId}`;
  const setDeliveryShareModalOpen = useSetRecoilState(
    simpleOpenAtomFamily(deliveryShareModalOpenId),
  );
  const [currentShareCoupon, setCurrentShareCoupon] = useState<GiftCouponForStore | null>(
    null,
  );
  const [couponShareOpenCount, setCouponShareOpenCount] = useState(0);

  const currentCouponShareUrl = useMemo(() => {
    if (!currentShareCoupon) return '';
    return `${window.location.origin}/gift-store/coupon/${currentShareCoupon.code}`;
  }, [currentShareCoupon]);

  const handleOpenCouponShareModal = useCallback(
    (coupon: GiftCouponForStore) => {
      setCurrentShareCoupon(coupon);
      setCouponShareOpenCount((c) => c + 1);
      setCouponShareModalOpen(true);
    },
    [setCouponShareModalOpen],
  );

  // === 전달 확인 모달 상태 ===
  const [showShareConfirm, setShowShareConfirm] = useState(false);
  const [pendingConfirmItem, setPendingConfirmItem] = useState<PendingConfirmItem | null>(
    null,
  );
  const [isConfirmSaving, setIsConfirmSaving] = useState(false);
  const [lastShareChannel, setLastShareChannel] = useState<ShareChannel>(
    ShareChannel.KakaoTalk,
  );
  const [confirmSeq, setConfirmSeq] = useState<number | null>(null);

  // === 카카오 공유 웹훅 확인 ===
  // 카카오톡 공유 시 백엔드가 웹훅 수신과 동시에 shareStatus(NotShared → Shared) +
  // kakaoSharedAt 을 자동 세팅하므로, 프론트는 가벼운 public 단건 조회 API 로
  // 그 두 필드를 폴링해서 확인만 한다 (POST /share/:id 호출 없음).
  //
  // 판별 기준:
  //   shareStatus !== NotShared && kakaoSharedAt != null
  //
  // 재전송 엣지 케이스: unshare 시 shareStatus 가 NotShared 로 리셋되므로,
  // 다음 카카오 시도 때 실제로 안 보내면 첫 조건이 fail → 오탐 없음.
  const kakaoShareCheck = useKakaoShareWebhookCheck({
    fetchShareState: async (kind, entityId) => {
      if (kind === 'coupon') {
        const c = await getGiftCouponById(entityId);
        return {
          isShared: c.shareStatus !== GiftCouponShareStatus.NotShared,
          kakaoSharedAt: c.kakaoSharedAt ? new Date(c.kakaoSharedAt) : null,
        };
      }
      const d = await getGiftDeliveryById(entityId);
      return {
        isShared: d.shareStatus !== GiftDeliveryShareStatus.NotShared,
        kakaoSharedAt: d.kakaoSharedAt ? new Date(d.kakaoSharedAt) : null,
      };
    },
    onSuccess: async (kind, entityId, snapshot) => {
      try {
        const detectedAt = snapshot.kakaoSharedAt ?? new Date();

        if (kind === 'coupon') {
          setOrderData((prev) => {
            if (!prev || !prev.coupons) return prev;
            return {
              ...prev,
              coupons: prev.coupons.map((c) =>
                c._id === entityId
                  ? {
                      ...c,
                      shareStatus:
                        c.shareStatus === GiftCouponShareStatus.NotShared
                          ? GiftCouponShareStatus.Shared
                          : c.shareStatus,
                      sharedAt: c.sharedAt ?? detectedAt,
                      kakaoSharedAt: detectedAt,
                      // 백엔드 웹훅이 lastSharedChannel 을 KakaoTalk 으로 덮어쓰므로 미러링.
                      lastSharedChannel: ShareChannel.KakaoTalk,
                    }
                  : c,
              ),
            };
          });
          const coupon = couponList.find((c) => c._id === entityId);
          if (coupon) {
            const seq = couponList.findIndex((c) => c._id === entityId) + 1;
            savePendingConfirm({
              type: 'coupon',
              itemId: entityId,
              seq,
              channel: ShareChannel.KakaoTalk,
              orderId,
              timestamp: Date.now(),
            });
            // 카톡 다녀와서 ShareConfirmModal 띄우기 전에 하단 공유 시트는 닫는다.
            setCouponShareModalOpen(false);
            setLastShareChannel(ShareChannel.KakaoTalk);
            setConfirmSeq(seq);
            setPendingConfirmItem({
              type: 'coupon',
              item: {
                ...coupon,
                kakaoSharedAt: detectedAt,
                lastSharedChannel: ShareChannel.KakaoTalk,
              },
            });
            setShowShareConfirm(true);
          }
        } else {
          setOrderData((prev) => {
            if (!prev || !prev.deliveries) return prev;
            return {
              ...prev,
              deliveries: prev.deliveries.map((d) =>
                d._id === entityId
                  ? {
                      ...d,
                      shareStatus:
                        d.shareStatus === GiftDeliveryShareStatus.NotShared
                          ? GiftDeliveryShareStatus.Shared
                          : d.shareStatus,
                      sharedAt: d.sharedAt ?? detectedAt,
                      kakaoSharedAt: detectedAt,
                      lastSharedChannel: GiftDeliveryShareChannel.KakaoTalk,
                    }
                  : d,
              ),
            };
          });
          const delivery = deliveryList.find((d) => d._id === entityId);
          if (delivery) {
            const seq = deliveryList.findIndex((d) => d._id === entityId) + 1;
            savePendingConfirm({
              type: 'delivery',
              itemId: entityId,
              seq,
              channel: ShareChannel.KakaoTalk,
              orderId,
              timestamp: Date.now(),
            });
            // 카톡 다녀와서 ShareConfirmModal 띄우기 전에 하단 공유 시트는 닫는다.
            setDeliveryShareModalOpen(false);
            setLastShareChannel(ShareChannel.KakaoTalk);
            setConfirmSeq(seq);
            setPendingConfirmItem({
              type: 'delivery',
              item: {
                ...delivery,
                kakaoSharedAt: detectedAt,
                lastSharedChannel: GiftDeliveryShareChannel.KakaoTalk,
              },
            });
            setShowShareConfirm(true);
          }
        }
        await syncCourtesyIfNeeded();
      } catch (error) {
        console.error('카카오 웹훅 확인 후 공유 상태 업데이트 실패:', error);
      }
    },
    onTimeout: () => {
      toastError('카카오톡 전송을 확인하지 못했어요. 다시 시도해 주세요.');
    },
  });

  const handleCouponShareComplete = useCallback(
    async (channel: ShareChannel) => {
      if (!currentShareCoupon) return;

      if (channel === ShareChannel.KakaoTalk) {
        kakaoShareCheck.start('coupon', currentShareCoupon._id);
        return;
      }

      const seq = couponList.findIndex((c) => c._id === currentShareCoupon._id) + 1;

      savePendingConfirm({
        type: 'coupon',
        itemId: currentShareCoupon._id,
        seq,
        channel,
        orderId,
        timestamp: Date.now(),
      });

      try {
        await Promise.all([
          shareCoupon(currentShareCoupon._id, role, channel),
          syncCourtesyIfNeeded(),
        ]);
        setOrderData((prev) => {
          if (!prev || !prev.coupons) return prev;
          return {
            ...prev,
            coupons: prev.coupons.map((c) =>
              c._id === currentShareCoupon._id
                ? {
                    ...c,
                    shareStatus: GiftCouponShareStatus.Shared,
                    // 백엔드가 share API 본문의 shareChannel 로 lastSharedChannel 을 갱신하므로 미러링.
                    lastSharedChannel: channel,
                  }
                : c,
            ),
          };
        });
      } catch (error) {
        console.error('공유 상태 업데이트 실패:', error);
      }

      setLastShareChannel(channel);
      setConfirmSeq(seq);
      setPendingConfirmItem({
        type: 'coupon',
        item: { ...currentShareCoupon, lastSharedChannel: channel },
      });
      // SMS / 다른 앱으로 보내고 돌아왔을 때도 하단 공유 시트는 닫고 확인 모달만 남긴다.
      setCouponShareModalOpen(false);
      setShowShareConfirm(true);
    },
    [
      currentShareCoupon,
      couponList,
      orderId,
      role,
      setOrderData,
      syncCourtesyIfNeeded,
      kakaoShareCheck,
      setCouponShareModalOpen,
    ],
  );

  const handleCouponShare = useCallback(
    (coupon: GiftCouponForStore) => {
      const dismissed = localStorage.getItem(SHARE_GUIDE_DISMISSED_KEY) === 'true';
      if (dismissed) {
        handleOpenCouponShareModal(coupon);
      } else {
        pendingShareRef.current = { type: 'coupon', item: coupon };
        setShowShareGuide(true);
      }
    },
    [handleOpenCouponShareModal],
  );

  // === 배송 공유 모달 (Individual) ===
  // openId / setOpen 은 위쪽 "쿠폰 / 배송 공유 모달" 블록에서 한 번에 선언함.
  const [currentShareDelivery, setCurrentShareDelivery] =
    useState<GiftDeliveryForStore | null>(null);
  const [deliveryShareOpenCount, setDeliveryShareOpenCount] = useState(0);

  const currentDeliveryShareUrl = useMemo(() => {
    if (!currentShareDelivery) return '';
    return `${window.location.origin}/gift-store/delivery/${currentShareDelivery.code}`;
  }, [currentShareDelivery]);

  const handleOpenDeliveryShareModal = useCallback(
    (delivery: GiftDeliveryForStore) => {
      setCurrentShareDelivery(delivery);
      setDeliveryShareOpenCount((c) => c + 1);
      setDeliveryShareModalOpen(true);
    },
    [setDeliveryShareModalOpen],
  );

  const handleDeliveryShareComplete = useCallback(
    async (channel: ShareChannel) => {
      if (!currentShareDelivery) return;

      if (channel === ShareChannel.KakaoTalk) {
        kakaoShareCheck.start('delivery', currentShareDelivery._id);
        return;
      }

      const seq = deliveryList.findIndex((d) => d._id === currentShareDelivery._id) + 1;

      savePendingConfirm({
        type: 'delivery',
        itemId: currentShareDelivery._id,
        seq,
        channel,
        orderId,
        timestamp: Date.now(),
      });

      const deliveryChannel = toDeliveryShareChannel(channel);

      try {
        await Promise.all([
          shareGiftDelivery(currentShareDelivery._id, role, deliveryChannel),
          syncCourtesyIfNeeded(),
        ]);
        setOrderData((prev) => {
          if (!prev || !prev.deliveries) return prev;
          return {
            ...prev,
            deliveries: prev.deliveries.map((d) =>
              d._id === currentShareDelivery._id
                ? {
                    ...d,
                    shareStatus: GiftDeliveryShareStatus.Shared,
                    lastSharedChannel: deliveryChannel,
                  }
                : d,
            ),
          };
        });
      } catch (error) {
        console.error('배송 공유 상태 업데이트 실패:', error);
      }

      setLastShareChannel(channel);
      setConfirmSeq(seq);
      setPendingConfirmItem({
        type: 'delivery',
        item: { ...currentShareDelivery, lastSharedChannel: deliveryChannel },
      });
      // SMS / 다른 앱으로 보내고 돌아왔을 때도 하단 공유 시트는 닫고 확인 모달만 남긴다.
      setDeliveryShareModalOpen(false);
      setShowShareConfirm(true);
    },
    [
      currentShareDelivery,
      deliveryList,
      orderId,
      role,
      setOrderData,
      syncCourtesyIfNeeded,
      kakaoShareCheck,
      setDeliveryShareModalOpen,
    ],
  );

  const handleDeliveryShare = useCallback(
    (delivery: GiftDeliveryForStore) => {
      const dismissed = localStorage.getItem(SHARE_GUIDE_DISMISSED_KEY) === 'true';
      if (dismissed) {
        handleOpenDeliveryShareModal(delivery);
      } else {
        pendingShareRef.current = { type: 'delivery', item: delivery };
        setShowShareGuide(true);
      }
    },
    [handleOpenDeliveryShareModal],
  );

  // 메모 남기기 모달
  const [showMemoModal, setShowMemoModal] = useState(false);
  const [pendingMemoItem, setPendingMemoItem] = useState<PendingConfirmItem | null>(null);
  const [isMemoSaving, setIsMemoSaving] = useState(false);

  // 전달 시 순번 (전체 쿠폰 목록에서의 index)
  const currentShareSeq = useMemo(() => {
    if (!currentShareCoupon) return null;
    const idx = couponList.findIndex((c) => c._id === currentShareCoupon._id);
    return idx >= 0 ? idx + 1 : null;
  }, [currentShareCoupon, couponList]);

  const currentDeliverySeq = useMemo(() => {
    if (!currentShareDelivery) return null;
    const idx = deliveryList.findIndex((d) => d._id === currentShareDelivery._id);
    return idx >= 0 ? idx + 1 : null;
  }, [currentShareDelivery, deliveryList]);

  // 페이지 진입 시 localStorage에서 미확인 전달 건 복원
  useEffect(() => {
    if (!orderId || !order) return;
    const pending = loadPendingConfirmForOrder(orderId);
    if (!pending) return;

    if (pending.type === 'coupon') {
      const coupon = couponList.find((c) => c._id === pending.itemId);
      if (coupon && !coupon.confirmedAt) {
        setPendingConfirmItem({ type: 'coupon', item: coupon });
        setLastShareChannel(pending.channel);
        setConfirmSeq(pending.seq);
        setShowShareConfirm(true);
      } else {
        removePendingConfirm(pending.itemId);
      }
    } else {
      const delivery = deliveryList.find((d) => d._id === pending.itemId);
      if (delivery && !delivery.confirmedAt) {
        setPendingConfirmItem({ type: 'delivery', item: delivery });
        setLastShareChannel(pending.channel);
        setConfirmSeq(pending.seq);
        setShowShareConfirm(true);
      } else {
        removePendingConfirm(pending.itemId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, order?._id]);

  const handleOpenMemoCoupon = useCallback((coupon: GiftCouponForStore) => {
    setPendingMemoItem({ type: 'coupon', item: coupon });
    setShowMemoModal(true);
  }, []);

  const handleOpenMemoDelivery = useCallback((delivery: GiftDeliveryForStore) => {
    setPendingMemoItem({ type: 'delivery', item: delivery });
    setShowMemoModal(true);
  }, []);

  const invalidateOrder = useCallback(() => {
    queryClient.invalidateQueries({
      predicate: (query) =>
        typeof query.queryKey === 'string' &&
        query.queryKey.includes(`/gift-order/${orderId}`),
    });
  }, [queryClient, orderId]);

  // "네, 보냈어요" → confirm API 호출 (memo optional) + 데이터 새로고침
  const handleShareConfirm = useCallback(
    async (memo: string) => {
      if (!pendingConfirmItem) return;
      setIsConfirmSaving(true);

      try {
        const trimmedMemo = memo.trim() || undefined;

        if (pendingConfirmItem.type === 'coupon') {
          await confirmCoupon(pendingConfirmItem.item._id, role, trimmedMemo);
        } else {
          await confirmDelivery(pendingConfirmItem.item._id, role, trimmedMemo);
        }
        removePendingConfirm(pendingConfirmItem.item._id);
        invalidateOrder();
      } catch (error) {
        console.error('전달 확인 실패:', error);
      } finally {
        setIsConfirmSaving(false);
        setShowShareConfirm(false);
        setPendingConfirmItem(null);
        setConfirmSeq(null);
      }
    },
    [pendingConfirmItem, role, invalidateOrder],
  );

  const performUnshare = useCallback(async () => {
    if (!pendingConfirmItem) return null;
    setIsConfirmSaving(true);
    const item = pendingConfirmItem;
    try {
      if (item.type === 'coupon') {
        await unshareCoupon(item.item._id, role);
        setOrderData((prev) => {
          if (!prev || !prev.coupons) return prev;
          return {
            ...prev,
            coupons: prev.coupons.map((c) =>
              c._id === item.item._id
                ? { ...c, shareStatus: GiftCouponShareStatus.NotShared }
                : c,
            ),
          };
        });
      } else {
        await unshareGiftDelivery(item.item._id, role);
        setOrderData((prev) => {
          if (!prev || !prev.deliveries) return prev;
          return {
            ...prev,
            deliveries: prev.deliveries.map((d) =>
              d._id === item.item._id
                ? { ...d, shareStatus: GiftDeliveryShareStatus.NotShared }
                : d,
            ),
          };
        });
      }
      removePendingConfirm(item.item._id);
    } catch (error) {
      console.error('공유 취소 실패:', error);
    } finally {
      setIsConfirmSaving(false);
      setShowShareConfirm(false);
      setPendingConfirmItem(null);
      setConfirmSeq(null);
    }
    return item;
  }, [pendingConfirmItem, role, setOrderData]);

  // "다른 방법으로 보내기" → unshare + 해당 아이템 공유 모달 바로 다시 열기
  const handleShareConfirmDeny = useCallback(async () => {
    const item = await performUnshare();
    if (!item) return;
    if (item.type === 'coupon') {
      handleOpenCouponShareModal(item.item as GiftCouponForStore);
    } else {
      handleOpenDeliveryShareModal(item.item as GiftDeliveryForStore);
    }
  }, [performUnshare, handleOpenCouponShareModal, handleOpenDeliveryShareModal]);

  // "나중에 할게요" → unshare만 하고 모달 닫기
  const handleShareConfirmLater = useCallback(async () => {
    await performUnshare();
  }, [performUnshare]);

  const handleShareConfirmClose = useCallback(() => {
    // localStorage에서 제거하지 않음 → 다음 페이지 진입 시 다시 표시됨
    setShowShareConfirm(false);
    setPendingConfirmItem(null);
    setConfirmSeq(null);
  }, []);

  /**
   * 카카오톡으로 잘못 보낸 케이스의 회수 플로우 (쿠폰 / 배송 공통).
   *
   * 흐름:
   *   1) `PATCH /:role/gift-coupon|gift-delivery/restricted-phone/:id` 로 수령자 번호 잠금
   *      → 백엔드는 `additionalRecipientPhone` 만 갱신, redeem 시 이 번호로만 본인 인증 허용
   *   2) 기존 공유 상태를 NotShared 로 리셋 (먼저 받은 분이 메시지를 그대로 가지고 있어도
   *      번호 잠금이 걸려 있어 수령이 불가)
   *   3) 확인 모달을 닫고, 곧바로 같은 답례품의 공유 시트를 다시 열어서 정정 발송 유도
   */
  const handleShareConfirmRecover = useCallback(
    async (phoneNumber: string, memo?: string) => {
      if (!pendingConfirmItem) return;
      setIsConfirmSaving(true);
      const trimmedMemo = memo?.trim() || undefined;
      try {
        if (pendingConfirmItem.type === 'coupon') {
          const coupon = pendingConfirmItem.item;
          await updateCouponRestrictedPhone(coupon._id, phoneNumber, role, trimmedMemo);
          await unshareCoupon(coupon._id, role);

          const updatedCoupon: GiftCouponForStore = {
            ...coupon,
            shareStatus: GiftCouponShareStatus.NotShared,
            kakaoSharedAt: undefined,
            additionalRecipientPhone: phoneNumber,
            memo: trimmedMemo ?? coupon.memo,
          };

          setOrderData((prev) => {
            if (!prev || !prev.coupons) return prev;
            return {
              ...prev,
              coupons: prev.coupons.map((c) =>
                c._id === coupon._id ? updatedCoupon : c,
              ),
            };
          });
          removePendingConfirm(coupon._id);

          setShowShareConfirm(false);
          setPendingConfirmItem(null);
          setConfirmSeq(null);
          handleOpenCouponShareModal(updatedCoupon);
        } else {
          const delivery = pendingConfirmItem.item;
          await updateDeliveryRestrictedPhone(
            delivery._id,
            phoneNumber,
            role,
            trimmedMemo,
          );
          await unshareGiftDelivery(delivery._id, role);

          const updatedDelivery: GiftDeliveryForStore = {
            ...delivery,
            shareStatus: GiftDeliveryShareStatus.NotShared,
            kakaoSharedAt: undefined,
            additionalRecipientPhone: phoneNumber,
            memo: trimmedMemo ?? delivery.memo,
          };

          setOrderData((prev) => {
            if (!prev || !prev.deliveries) return prev;
            return {
              ...prev,
              deliveries: prev.deliveries.map((d) =>
                d._id === delivery._id ? updatedDelivery : d,
              ),
            };
          });
          removePendingConfirm(delivery._id);

          setShowShareConfirm(false);
          setPendingConfirmItem(null);
          setConfirmSeq(null);
          handleOpenDeliveryShareModal(updatedDelivery);
        }
      } catch (error) {
        console.error('회수(번호 잠금) 실패:', error);
        toastError('번호 저장에 실패했어요. 잠시 후 다시 시도해 주세요.');
      } finally {
        setIsConfirmSaving(false);
      }
    },
    [
      pendingConfirmItem,
      role,
      setOrderData,
      handleOpenCouponShareModal,
      handleOpenDeliveryShareModal,
    ],
  );

  // ============================================================
  // 휴대폰번호 잠금 수정 (PhoneLockEditModal)
  // ============================================================
  // 잘못 보낸 케이스에서 회수 플로우로 잠긴 번호를 사후에 정정하거나,
  // 공유된 답례품에 예방 차원의 번호 잠금을 추가하기 위한 별도 진입점.
  //
  // - 동일 endpoint (restricted-phone PATCH) 재사용
  // - 해제는 지원 X (UX 결정) — 다른 번호로 변경만 가능
  // - 회수 플로우와 달리 unshare/공유 시트 재오픈은 하지 않는다 (단순 갱신)
  const [phoneLockTarget, setPhoneLockTarget] = useState<PendingConfirmItem | null>(null);
  const [showPhoneLockEdit, setShowPhoneLockEdit] = useState(false);
  const [isPhoneLockSaving, setIsPhoneLockSaving] = useState(false);

  const handleOpenPhoneLockEdit = useCallback(
    (item: GiftCouponForStore | GiftDeliveryForStore, kind: 'coupon' | 'delivery') => {
      setPhoneLockTarget(
        kind === 'coupon'
          ? { type: 'coupon', item: item as GiftCouponForStore }
          : { type: 'delivery', item: item as GiftDeliveryForStore },
      );
      setShowPhoneLockEdit(true);
    },
    [],
  );

  const handleClosePhoneLockEdit = useCallback(() => {
    if (isPhoneLockSaving) return;
    setShowPhoneLockEdit(false);
    setPhoneLockTarget(null);
  }, [isPhoneLockSaving]);

  const handlePhoneLockSave = useCallback(
    async (phoneNumber: string, memo?: string) => {
      if (!phoneLockTarget) return;
      setIsPhoneLockSaving(true);
      const trimmedMemo = memo?.trim() || undefined;
      try {
        if (phoneLockTarget.type === 'coupon') {
          const coupon = phoneLockTarget.item;
          await updateCouponRestrictedPhone(coupon._id, phoneNumber, role, trimmedMemo);
          setOrderData((prev) => {
            if (!prev || !prev.coupons) return prev;
            return {
              ...prev,
              coupons: prev.coupons.map((c) =>
                c._id === coupon._id
                  ? {
                      ...c,
                      additionalRecipientPhone: phoneNumber,
                      memo: trimmedMemo ?? c.memo,
                    }
                  : c,
              ),
            };
          });
        } else {
          const delivery = phoneLockTarget.item;
          await updateDeliveryRestrictedPhone(
            delivery._id,
            phoneNumber,
            role,
            trimmedMemo,
          );
          setOrderData((prev) => {
            if (!prev || !prev.deliveries) return prev;
            return {
              ...prev,
              deliveries: prev.deliveries.map((d) =>
                d._id === delivery._id
                  ? {
                      ...d,
                      additionalRecipientPhone: phoneNumber,
                      memo: trimmedMemo ?? d.memo,
                    }
                  : d,
              ),
            };
          });
        }
        setShowPhoneLockEdit(false);
        setPhoneLockTarget(null);
      } catch (error) {
        console.error('번호 잠금 수정 실패:', error);
        toastError('번호 저장에 실패했어요. 잠시 후 다시 시도해 주세요.');
      } finally {
        setIsPhoneLockSaving(false);
      }
    },
    [phoneLockTarget, role, setOrderData],
  );

  const handleMemoSave = useCallback(
    async (memo: string) => {
      if (!pendingMemoItem) return;
      const trimmed = memo.trim();
      if (!trimmed) return;
      setIsMemoSaving(true);
      try {
        if (pendingMemoItem.type === 'coupon') {
          await confirmCoupon(pendingMemoItem.item._id, role, trimmed);
        } else {
          await confirmDelivery(pendingMemoItem.item._id, role, trimmed);
        }
        invalidateOrder();
      } catch (error) {
        console.error('메모 저장 실패:', error);
      } finally {
        setIsMemoSaving(false);
        setShowMemoModal(false);
        setPendingMemoItem(null);
      }
    },
    [pendingMemoItem, role, invalidateOrder],
  );

  const handleMemoClose = useCallback(() => {
    setShowMemoModal(false);
    setPendingMemoItem(null);
  }, []);

  // === 하단 버튼 (쿠폰 + 배송 통합) ===
  const handleBottomButtonClick = useCallback(() => {
    const firstUnsharedCoupon = couponList.find(
      (c) => c.shareStatus === GiftCouponShareStatus.NotShared,
    );
    if (firstUnsharedCoupon) {
      handleCouponShare(firstUnsharedCoupon);
      return;
    }

    if (isIndividualDelivery) {
      const firstUnsharedDelivery = deliveryList.find(
        (d) => d.shareStatus === GiftDeliveryShareStatus.NotShared,
      );
      if (firstUnsharedDelivery) {
        handleDeliveryShare(firstUnsharedDelivery);
      }
    }
  }, [
    couponList,
    deliveryList,
    isIndividualDelivery,
    handleCouponShare,
    handleDeliveryShare,
  ]);

  const handleShareGuideConfirm = useCallback(() => {
    setShowShareGuide(false);
    if (pendingShareRef.current) {
      if (pendingShareRef.current.type === 'coupon') {
        handleOpenCouponShareModal(pendingShareRef.current.item as GiftCouponForStore);
      } else {
        handleOpenDeliveryShareModal(
          pendingShareRef.current.item as GiftDeliveryForStore,
        );
      }
      pendingShareRef.current = null;
    }
  }, [handleOpenCouponShareModal, handleOpenDeliveryShareModal]);

  // === 통계 (재발행/재발급 쿠폰 제외) ===
  const originalCouponList = couponList.filter((c) => !c.isAdditional && !c.isReissued);
  const totalCoupons = originalCouponList.length;
  const sharedCoupons = originalCouponList.filter(
    (c) => c.shareStatus !== GiftCouponShareStatus.NotShared,
  ).length;
  const unsharedCoupons = totalCoupons - sharedCoupons;

  const totalIndividualDeliveries = isIndividualDelivery ? deliveryList.length : 0;
  const sharedDeliveries = isIndividualDelivery
    ? deliveryList.filter((d) => d.shareStatus !== GiftDeliveryShareStatus.NotShared)
        .length
    : 0;
  const unsharedDeliveries = totalIndividualDeliveries - sharedDeliveries;

  const hasShareableItems = couponList.length > 0 || isIndividualDelivery;
  const totalUnshared = unsharedCoupons + unsharedDeliveries;

  // ShareConfirmModal 의 톤(확정형 vs 추정형)을 결정하기 위해 노출.
  // 카톡 웹훅이 도착해 백엔드가 실제 전달을 확인했으면 값이 들어있다.
  const confirmKakaoSharedAt: Date | null = pendingConfirmItem?.item.kakaoSharedAt
    ? new Date(pendingConfirmItem.item.kakaoSharedAt)
    : null;
  const confirmItemKind: 'coupon' | 'delivery' | null = pendingConfirmItem
    ? pendingConfirmItem.type
    : null;
  // 마지막 공유 채널(백엔드 기록). ShareChannel / GiftDeliveryShareChannel 모두
  // 'KakaoTalk' 문자열 값을 공유하므로 모달에서는 단순 문자열 비교로 사용한다.
  const confirmLastSharedChannel: string | null =
    pendingConfirmItem?.item.lastSharedChannel ?? null;
  // 재전달 케이스에서 confirm 모달 메모 입력에 prefill 하기 위해 노출.
  const confirmExistingMemo: string | undefined = pendingConfirmItem?.item.memo;
  // 회수 플로우 또는 예방 잠금으로 잠금된 번호. 모달에서 부가 정보로 표시.
  const confirmAdditionalRecipientPhone: string | undefined =
    pendingConfirmItem?.item.additionalRecipientPhone;

  return {
    couponList,
    deliveryItems,
    deliveryList,
    isIndividualDelivery,
    isBulkDelivery,

    couponShareModalOpenId,
    couponShareOpenCount,
    currentCouponShareUrl,
    currentShareSeq,
    currentShareCoupon,
    handleCouponShare,
    handleCouponShareComplete,

    deliveryShareModalOpenId,
    deliveryShareOpenCount,
    currentDeliveryShareUrl,
    currentDeliverySeq,
    currentShareDelivery,
    handleDeliveryShare,
    handleDeliveryShareComplete,

    showShareGuide,
    setShowShareGuide,
    handleBottomButtonClick,
    handleShareGuideConfirm,

    showShareConfirm,
    isConfirmSaving,
    lastShareChannel,
    confirmSeq,
    confirmKakaoSharedAt,
    confirmItemKind,
    confirmLastSharedChannel,
    confirmExistingMemo,
    confirmAdditionalRecipientPhone,
    handleShareConfirm,
    handleShareConfirmDeny,
    handleShareConfirmLater,
    handleShareConfirmClose,
    handleShareConfirmRecover,

    showMemoModal,
    isMemoSaving,
    memoExistingValue: pendingMemoItem?.item.memo,
    handleOpenMemoCoupon,
    handleOpenMemoDelivery,
    handleMemoSave,
    handleMemoClose,

    // PhoneLockEditModal
    showPhoneLockEdit,
    isPhoneLockSaving,
    phoneLockTarget,
    handleOpenPhoneLockEdit,
    handlePhoneLockSave,
    handleClosePhoneLockEdit,

    totalCoupons,
    sharedCoupons,
    unsharedCoupons,
    totalIndividualDeliveries,
    sharedDeliveries,
    unsharedDeliveries,
    hasShareableItems,
    totalUnshared,
  };
}
